import type { QueryClient, QueryKey } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import NotesV2ApiService from '../../services/notesV2Api';
import type {
  CreateNoteV2Data,
  UpdateNoteV2Data,
  NoteV2,
  NotesV2Response,
} from '../../services/notesV2Api';

/**
 * Offline mutation defaults — the heart of "write while offline, sync on
 * reconnect."
 *
 * Why a global registry instead of inline `useMutation({ mutationFn })`:
 * a mutation that runs while offline is *paused* and persisted to IndexedDB.
 * After an app reload, React Query restores it with only its `mutationKey` and
 * `variables` — every closure (including `mutationFn`) is gone. Registering the
 * function against a stable key here lets `resumePausedMutations()` re-attach it
 * and replay the write. For the same reason, anything the write needs (like
 * `workspaceId`) must live inside `variables`, not in a closure.
 *
 * Each default also owns its optimistic update (`onMutate`), rollback
 * (`onError`), and reconciliation (`onSettled`), so a live mutation and a
 * resumed-after-reload one behave identically.
 */

export const notesMutationKeys = {
  create: ['notes', 'create'] as const,
  update: ['notes', 'update'] as const,
  delete: ['notes', 'delete'] as const,
};

// `workspaceId` travels in the variables so the write is fully self-describing
// and replayable after a reload (see the module comment above).
export interface CreateNoteVars {
  workspaceId: string;
  noteData: CreateNoteV2Data;
}
export interface UpdateNoteVars {
  workspaceId: string;
  noteId: string;
  noteData: UpdateNoteV2Data;
}
export interface DeleteNoteVars {
  workspaceId: string;
  noteId: string;
}

// What onMutate stashes so onError can restore the cache. We snapshot every
// cached page/filter combination for the workspace, not just one.
interface NotesRollbackContext {
  snapshot: [QueryKey, NotesV2Response | undefined][];
}

const listKeyFor = (workspaceId: string): QueryKey => ['workspace-notes-v2', workspaceId];

/**
 * Build the placeholder note shown immediately while a create is offline /
 * in-flight, before the server assigns the real row.
 *
 * ── This is the spot worth your judgment. ──────────────────────────────────
 * The `author` is the interesting decision: optimistically we don't have the
 * server's view of the user, so this ships a generic "You" placeholder. If you
 * want the real display name to appear instantly, pull it from wherever the
 * current user lives (e.g. the cached ['pro-profile'] / ['citizen-profile']
 * query, or an auth context) and set it here. Same goes for how you want
 * entities labelled and whether to expose a "pending" flag to the UI.
 *
 * The `optimistic-` id prefix is deliberate: UI can detect a not-yet-synced
 * note via `note.id.startsWith('optimistic-')` if you want a pending badge.
 */
function buildOptimisticNote(noteData: CreateNoteV2Data): NoteV2 {
  const now = new Date().toISOString();
  return {
    id: `optimistic-${crypto.randomUUID()}`,
    title: noteData.title,
    content: noteData.content,
    include_in_reports: noteData.include_in_reports ?? false,
    created_at: now,
    updated_at: now,
    version: 0,
    author: { username: null, display_name: 'You' },
    entities: (noteData.entities ?? []).map((entity) => ({
      entity_type: entity.entity_type,
      entity_type_display: entity.entity_type,
      entity_id: entity.entity_id,
      entity_display_name: entity.entity_display_name ?? '',
    })),
    tags: noteData.tags ?? [],
  };
}

/** Reconcile with server truth after a write settles (last-write-wins). */
function invalidateNotes(queryClient: QueryClient, workspaceId: string): void {
  // Offline this is a no-op refetch (networkMode keeps cache); it runs for real
  // once connectivity returns and the mutation has been replayed.
  queryClient.invalidateQueries(listKeyFor(workspaceId));
  queryClient.invalidateQueries(['entity-notes-v2']);
  // The tag vocabulary is derived from the notes themselves, so a write can
  // introduce a tag (or retire the last use of one).
  queryClient.invalidateQueries(['workspace-note-tags', workspaceId]);
}

/** Restore the pre-mutation cache when a write ultimately fails. */
function rollback(queryClient: QueryClient, context: unknown): void {
  const snapshot = (context as NotesRollbackContext | undefined)?.snapshot;
  snapshot?.forEach(([key, data]) => queryClient.setQueryData(key, data));
}

/**
 * Register offline-capable defaults for the Notes mutations. Call once at
 * startup, before `resumePausedMutations()` can run.
 */
export function registerOfflineMutationDefaults(queryClient: QueryClient): void {
  // ── CREATE ──────────────────────────────────────────────────────────────
  queryClient.setMutationDefaults(notesMutationKeys.create, {
    mutationFn: ({ workspaceId, noteData }: CreateNoteVars) =>
      NotesV2ApiService.createNote(workspaceId, noteData),
    onMutate: async ({ workspaceId, noteData }: CreateNoteVars): Promise<NotesRollbackContext> => {
      const listKey = listKeyFor(workspaceId);
      // Cancel in-flight refetches so they can't clobber the optimistic state.
      await queryClient.cancelQueries(listKey);
      const snapshot = queryClient.getQueriesData<NotesV2Response>(listKey);
      const optimisticNote = buildOptimisticNote(noteData);
      queryClient.setQueriesData<NotesV2Response>(listKey, (old) =>
        old ? { ...old, notes: [optimisticNote, ...old.notes] } : old,
      );
      return { snapshot };
    },
    onError: (_err, _vars: CreateNoteVars, context) => {
      rollback(queryClient, context);
      toast.error('Failed to create note');
    },
    onSuccess: () => {
      toast.success('Note created successfully');
    },
    onSettled: (_data, _err, { workspaceId }: CreateNoteVars) => {
      invalidateNotes(queryClient, workspaceId);
    },
  });

  // ── UPDATE ──────────────────────────────────────────────────────────────
  queryClient.setMutationDefaults(notesMutationKeys.update, {
    mutationFn: ({ workspaceId, noteId, noteData }: UpdateNoteVars) =>
      NotesV2ApiService.updateNote(workspaceId, noteId, noteData),
    onMutate: async ({ workspaceId, noteId, noteData }: UpdateNoteVars): Promise<NotesRollbackContext> => {
      const listKey = listKeyFor(workspaceId);
      await queryClient.cancelQueries(listKey);
      const snapshot = queryClient.getQueriesData<NotesV2Response>(listKey);
      queryClient.setQueriesData<NotesV2Response>(listKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          notes: old.notes.map((note) =>
            note.id === noteId
              ? {
                  ...note,
                  title: noteData.title ?? note.title,
                  content: noteData.content,
                  include_in_reports: noteData.include_in_reports ?? note.include_in_reports,
                  tags: noteData.tags ?? note.tags,
                  updated_at: new Date().toISOString(),
                }
              : note,
          ),
        };
      });
      return { snapshot };
    },
    onError: (_err, _vars: UpdateNoteVars, context) => {
      rollback(queryClient, context);
      toast.error('Failed to update note');
    },
    onSuccess: () => {
      toast.success('Note updated successfully');
    },
    onSettled: (_data, _err, { workspaceId }: UpdateNoteVars) => {
      invalidateNotes(queryClient, workspaceId);
    },
  });

  // ── DELETE ──────────────────────────────────────────────────────────────
  queryClient.setMutationDefaults(notesMutationKeys.delete, {
    mutationFn: ({ workspaceId, noteId }: DeleteNoteVars) =>
      NotesV2ApiService.deleteNote(workspaceId, noteId),
    onMutate: async ({ workspaceId, noteId }: DeleteNoteVars): Promise<NotesRollbackContext> => {
      const listKey = listKeyFor(workspaceId);
      await queryClient.cancelQueries(listKey);
      const snapshot = queryClient.getQueriesData<NotesV2Response>(listKey);
      queryClient.setQueriesData<NotesV2Response>(listKey, (old) =>
        old ? { ...old, notes: old.notes.filter((note) => note.id !== noteId) } : old,
      );
      return { snapshot };
    },
    onError: (_err, _vars: DeleteNoteVars, context) => {
      rollback(queryClient, context);
      toast.error('Failed to delete note');
    },
    onSuccess: () => {
      toast.success('Note deleted successfully');
    },
    onSettled: (_data, _err, { workspaceId }: DeleteNoteVars) => {
      invalidateNotes(queryClient, workspaceId);
    },
  });
}
